import React, { FC, useState, ChangeEvent, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Button,
    Box,
    Grid,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    TextField,
    FormGroup,
    FormControlLabel,
    Checkbox,
    SelectChangeEvent,
} from '@mui/material';
import DHDialog from 'components/shared/dh-dialog/dh-dialog.component';
import ApprovedByAutocomplete from 'components/orders/order-details/approved-by-autocomplete/approved-by-autocomplete.component';
import { getEscapedMessage } from 'utils/escaped-message';
import { User } from 'models/user';
import { DeviationType } from 'models/deviation';
import { Deviation } from 'models/deviations';
import { Organization } from 'models/organization';

type AddUpdateDeviationDialogProps = {
    open: boolean;
    onDialogClose: () => void;
    currentUser: User;
    deviation?: Deviation;
    onDeviationAdd: (deviation: Deviation) => void;
    onDeviationUpdate: (deviation: Deviation) => void;
};

const AddUpdateDeviationDialog: FC<AddUpdateDeviationDialogProps> = ({
    open,
    onDialogClose,
    currentUser,
    deviation,
    onDeviationAdd,
    onDeviationUpdate,
}: AddUpdateDeviationDialogProps) => {
    const { t: translate } = useTranslation(['common', 'deviations']);
    const [labTechnician, setLabTechnician] = useState<User>({} as User);
    const [visibleForCustomer, setVisibleForCustomer] = useState(false);
    const [report, setReport] = useState('');
    const [selectedDeviationType, setSelectedDeviationType] = useState<DeviationType>({} as DeviationType);

    useEffect(() => {
        if (deviation?.addedOn) {
            setLabTechnician(deviation.technician as any);
            // DD-3494 rename: new field first, deprecated field as fallback until backfill.
            setVisibleForCustomer(Boolean(deviation.visibleForCustomer ?? deviation.visibleForDentist));
            setReport(deviation.report?.replace(new RegExp('<br/>', 'gi'), '\n'));
            const filterData = (currentUser.organization as Organization).deviationTypes?.find(
                (deviationType) => deviationType.code === deviation.code,
            );
            setSelectedDeviationType(filterData as DeviationType);
        }
    }, [deviation]);

    const isEmpty = <T extends object>(obj: T): boolean => {
        return Object.keys(obj).length === 0;
    };

    const handleDialogClose = () => {
        onDialogClose();
        setLabTechnician({} as User);
        setVisibleForCustomer(false);
        setReport('');
        setSelectedDeviationType({} as DeviationType);
    };

    const selectTechnician = (technician: User | any) => {
        setLabTechnician(technician);
    };

    const handleVisibleForCustomerCheckbox = (event: ChangeEvent<HTMLInputElement>) => {
        setVisibleForCustomer(event.target.checked);
    };

    const handleReportChange = (event: ChangeEvent<HTMLInputElement>) => {
        setReport(event.target.value);
    };

    const handleDeviationTypeChange = (event: SelectChangeEvent<string>) => {
        const value = event.target.value;
        const filterData = (currentUser.organization as Organization).deviationTypes?.find(
            (deviationType) => deviationType._id === value,
        );
        setSelectedDeviationType(filterData as DeviationType);
    };

    const handleAddDeviation = () => {
        const formattedReport = getEscapedMessage(report);
        const data = {
            code: selectedDeviationType.code,
            description: selectedDeviationType.description,
            type: selectedDeviationType.type,
            technician: labTechnician.fullName,
            report: formattedReport,
            addedOn: new Date(),
            // PR 1 of DD-3494 still sends the deprecated input field; the backend
            // dual-writes both. The mutation switches to visibleForCustomer once
            // the backend schema change is deployed (PR 2).
            visibleForDentist: visibleForCustomer,
        };
        onDeviationAdd(data);
        handleDialogClose();
    };

    const handleUpdateDeviation = () => {
        const formattedReport = getEscapedMessage(report);
        const data = {
            _id: deviation?._id,
            code: selectedDeviationType.code,
            description: selectedDeviationType.description,
            type: selectedDeviationType.type,
            technician: labTechnician.fullName || (deviation?.technician as string),
            report: formattedReport,
            addedOn: new Date(),
            // PR 1 of DD-3494: deprecated input field, dual-written server-side.
            visibleForDentist: visibleForCustomer,
        };
        onDeviationUpdate(data);
        handleDialogClose();
    };

    return (
        <DHDialog
            maxWidth="lg"
            fullWidth
            open={open}
            onClose={handleDialogClose}
            title={deviation?._id ? translate('deviations:UPDATE_DEVIATION') : translate('deviations:ADD_DEVIATION')}
            actions={
                <Button
                    key="submit"
                    disabled={isEmpty(selectedDeviationType) || isEmpty(labTechnician) || !report}
                    onClick={deviation?._id ? handleUpdateDeviation : handleAddDeviation}
                    variant="contained"
                    type="submit"
                    data-test="add-update-deviation-dialog.save-btn"
                >
                    {deviation?._id ? translate('common:UPDATE') : translate('common:ADD')}
                </Button>
            }
        >
            <Box mt={1}>
                <Grid container spacing={1}>
                    <Grid item xs={12}>
                        <FormControl fullWidth>
                            <InputLabel id="deviation-type-label">{translate('deviations:DEVIATION_TYPE')}</InputLabel>
                            <Select
                                labelId="deviation-type-label"
                                label={translate('deviations:DEVIATION_TYPE')}
                                value={selectedDeviationType?._id || ''}
                                onChange={handleDeviationTypeChange}
                                data-test="add-update-deviation-dialog.deviation-type-select"
                            >
                                {(currentUser.organization as Organization).deviationTypes?.map((deviationType) => (
                                    <MenuItem key={deviationType._id} value={deviationType._id}>
                                        {deviationType.code} - {deviationType.description}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={6}>
                        <ApprovedByAutocomplete
                            onTechnicianSelected={selectTechnician}
                            labTechnician={labTechnician}
                            dataTest="add-update-deviation-dialog.lab-technician-autocomplete"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            margin="dense"
                            label={translate('deviations:DEVIATION_REPORT')}
                            onChange={handleReportChange}
                            value={report}
                            multiline
                            rows={12}
                            data-test="add-update-deviation-dialog.deviation-report-textfield"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <FormGroup>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={visibleForCustomer}
                                        onChange={handleVisibleForCustomerCheckbox}
                                        data-test="add-update-deviation-dialog.visible-for-dentist-checkbox"
                                    />
                                }
                                label={translate('deviations:VISIBLE_FOR_CUSTOMER_CHECKBOX')}
                            />
                        </FormGroup>
                    </Grid>
                </Grid>
            </Box>
        </DHDialog>
    );
};

export default AddUpdateDeviationDialog;
